/**
 * アフィリエイト・リンク装飾層（純粋関数・テスト可能）。
 *
 * ★設計原則（この案件の「推測しない」規律に合わせる）:
 *  1. 単一チョークポイント: 外部への送客URLはすべて decorate() を通す。収益化の配線はここ1か所だけ。
 *  2. 未設定なら素通し: 対応するアフィリIDが無ければURLを一切変えない（＝現状と同じ挙動・リンクは壊れない）。
 *     ＝この層を入れても、代表がIDを入れるまでは「素のリンク（無報酬）」のまま安全に出せる。
 *  3. 形式を推測しない: 各ASPの実リンク形式は「公式に公開された安定フォーマット」だけ実装。
 *     形式を確認できないサービス（Skyscanner=Travelpayouts等）は STRATEGIES に載せず素通しにする。
 *     ＝誤ったwrapで「計測したつもりの空振り/リンク切れ」を作るより、無装飾で送客する方が安全。
 *
 * ★収益化には2系統ある（重複させない）:
 *  - ホテル予約URL … 楽天トラベルAPIが affiliateId 設定時に自動でアフィリンク化する（サーバ側の
 *    RAKUTEN_AFFILIATE_ID / functions/api/hotels.ts）。＝この層では装飾しない（二重化防止）。
 *  - レンタカー/航空券の送客URL … この層 decorate() が装飾する（クライアント側・deeplinks.ts の出力）。
 *
 * ★アフィリIDは秘密ではない: 送客URLに必ず露出する公開値。public/affiliate-config.js に置き、
 *   実行時グローバル window.RVP_AFFILIATE から読む（未設定は {} ＝全素通し＝収益化オフ）。
 */

export interface ValueCommerceLink {
  /** サイトID（バリューコマース管理画面）。 */
  sid: string;
  /** 広告主プログラムID（提携ごとに異なる）。 */
  pid: string;
  /**
   * VCがMyLink生成時に遷移先URLへ自動付与する、広告主側の流入元パラメータ（クエリ文字列そのまま）。
   * 実物から採取: じゃらん=`vos=afjlrpvczzzzx00000001` / るるぶ=`utm_source=valuecommerce&utm_medium=affiliate`
   * ＝広告主ごとに形式が違うので、キー名を決め打ちせず**そのまま持つ**。
   * ★ここに置く理由: 素通し（sid/pid未設定）のときに他社の計測コードが付いたURLを出さないため。
   *   遷移先URLそのものに埋めてしまうと「未設定なら一切URLを変えない」という本モジュールの不変条件を壊す。
   */
  params?: string;
}

export interface AffiliateConfig {
  /**
   * 楽天アフィリID（楽天アフィリエイト管理画面の「アフィリエイトID」）。
   * 楽天レンタカー等の送客URLを hb.afl 経由（計測付き）にする。ホテル予約URLはサーバ側が担当。
   */
  rakutenAffiliateId?: string;
  /**
   * バリューコマース: serviceId ごとの提携（じゃらん等はVC経由）。
   * キー=serviceId（例 'jalan_rentacar'）／値={ sid, pid }。提携のある serviceId だけ入れる。
   */
  valueCommerce?: Record<string, ValueCommerceLink>;
  /**
   * Travelpayouts の Partner ID（旧称 marker）。Aviasales（航空券検索）の送客URLに付与。
   * ★Travelpayoutsの実体はSkyscanner本体ではなくAviasales（同持株会社の別ブランド）。
   */
  travelpayoutsMarker?: string;
}

/** 装飾ストラテジ: URLを装飾して返す。ID未設定/対象外なら null（＝呼び出し側で素通し）。 */
type Strategy = (url: string, cfg: AffiliateConfig) => string | null;

/**
 * 楽天アフィリエイト リンク形式（公式・安定）:
 *   https://hb.afl.rakuten.co.jp/hgc/<アフィリID>/?pc=<遷移先URL>&m=<遷移先URL>
 * ＝任意の楽天URLをアフィリ計測付きに包む標準形。<アフィリID> は代表がアフィリエイト管理画面で発行する公開値。
 */
function rakutenAfl(url: string, cfg: AffiliateConfig): string | null {
  const id = cfg.rakutenAffiliateId?.trim();
  if (!id) return null;
  const enc = encodeURIComponent(url);
  return `https://hb.afl.rakuten.co.jp/hgc/${id}/?pc=${enc}&m=${enc}`;
}

/**
 * バリューコマース MyLink（vc_url 直リンク）形式:
 *   https://ck.jp.ap.valuecommerce.com/servlet/referral?sid=<sid>&pid=<pid>&vc_url=<遷移先URL>
 * sid/pid は提携（広告主プログラム）ごと。代表がVC管理画面で「MyLink」から取得して config に入れる。
 * ※直リンク（vc_url）可否は広告主の許諾に依存。許諾外なら sid/pid を入れない＝素通しのまま。
 */
function valueCommerce(serviceId: string): Strategy {
  return (url, cfg) => {
    const vc = cfg.valueCommerce?.[serviceId];
    if (!vc?.sid?.trim() || !vc?.pid?.trim()) return null;
    // 広告主側の流入元パラメータは「装飾するときだけ」遷移先に足す（素通し時のURLは1文字も変えない）。
    // VCが発行した文字列をそのまま連結する（再エンコードするとVC発行のリンクと一致しなくなる）。
    const extra = vc.params?.trim().replace(/^[?&]/, '');
    const target = extra ? `${url}${url.includes('?') ? '&' : '?'}${extra}` : url;
    return (
      'https://ck.jp.ap.valuecommerce.com/servlet/referral' +
      `?sid=${encodeURIComponent(vc.sid.trim())}&pid=${encodeURIComponent(vc.pid.trim())}` +
      `&vc_url=${encodeURIComponent(target)}`
    );
  };
}

/**
 * Travelpayouts（Aviasales航空券検索）の marker 付与:
 *   遷移先URLの末尾に &marker=<Partner ID>（既にqueryがあれば&、無ければ?）を追記する形。
 *   公式ドキュメントのWhite Label URL例（?marker=YOUR_ID.subID）と同型。Partner IDは登録時に自動発行される公開値。
 */
function travelpayoutsMarker(url: string, cfg: AffiliateConfig): string | null {
  const marker = cfg.travelpayoutsMarker?.trim();
  if (!marker) return null;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}marker=${encodeURIComponent(marker)}`;
}

/**
 * serviceId → 装飾ストラテジ。ここに無いサービスは素通し（＝計測しない）。
 * ★実リンク形式を公式で確認できたASPだけ登録する。
 *   - Skyscanner: Skyscanner自体のアフィリは2018年終了（Travelpayouts経由でも取得不可） → 未登録（素通し）。
 *   - Aviasales: Travelpayoutsの実体（同持株会社ブランド）。marker形式確認済み → 登録。
 *   - Google（フライト/マップ/ホテル）: アフィリなし → 素通し。
 *   - ANA/JAL 国内線単体: 送客アフィリの提供が乏しい → 素通し。
 *   - たびらい/トヨタ/ニッポン レンタカー: 提携/形式が未確定 → 素通し（提携できたらVC等で追加）。
 */
const STRATEGIES: Record<string, Strategy> = {
  rakuten_rentacar: rakutenAfl,
  jalan_rentacar: valueCommerce('jalan_rentacar'),
  // たびらい（レンタカー比較）。2026-08-01に条件プリセット可能と実測できたので導線を格上げした。
  // 提携できたらVC等で装飾できるよう受け皿だけ置く（未提携なら素通し＝無報酬）。
  tabirai_rentacar: valueCommerce('tabirai_rentacar'),
  // じゃらん宿泊。2026-08-01に条件プリセット可能と実測できたので導線を追加した（提携は代表が別途）。
  jalan_hotel: valueCommerce('jalan_hotel'),
  // るるぶトラベル（JTB系・国内宿専門）もバリューコマース経由。宿は料率が楽天(1%)・じゃらん(1.1%)より高い。
  rurubu_hotel: valueCommerce('rurubu_hotel'),
  /*
   * 以下3件は2026-07-31に代表が提携した案件（VC側の反映待ち）。
   * 受け皿だけ先に置いてある＝設定にpidが入った瞬間に装飾が効く。UIへのリンク設置は別途。
   *   yahoo_package … 宿＋航空券のパッケージ（離島の射場は空路＋宿泊なので相性がよい）
   *   asoview / activityjapan … 観光・体験（射場ガイドの「周辺の見どころ」枠を想定）
   */
  yahoo_package: valueCommerce('yahoo_package'),
  asoview: valueCommerce('asoview'),
  activityjapan: valueCommerce('activityjapan'),
  aviasales: travelpayoutsMarker,
};

/**
 * 外部送客URLを装飾する単一チョークポイント。
 * 未対応サービス・未設定・空URL・'#'（デモ）は url をそのまま返す（＝壊さない）。
 */
export function decorate(serviceId: string, url: string, cfg: AffiliateConfig): string {
  if (!url || url === '#') return url;
  const strat = STRATEGIES[serviceId];
  if (!strat) return url;
  return strat(url, cfg) ?? url;
}

/** その serviceId が現在の設定で装飾（＝収益化）対象になるか。UIの「広告」判定に使える。 */
export function isAffiliated(serviceId: string, cfg: AffiliateConfig): boolean {
  const strat = STRATEGIES[serviceId];
  return !!strat && strat('https://example.com/', cfg) != null;
}

/** 何らかのアフィリが有効設定済みか（＝収益化オン）。開示表示の出し分けに使う。 */
export function hasAnyAffiliate(cfg: AffiliateConfig): boolean {
  if (cfg.rakutenAffiliateId?.trim()) return true;
  if (cfg.travelpayoutsMarker?.trim()) return true;
  return Object.values(cfg.valueCommerce ?? {}).some((v) => !!v?.sid?.trim() && !!v?.pid?.trim());
}

/** window.RVP_AFFILIATE を安全に読む（未設定/型不正/例外は {} ＝全素通し）。 */
export function loadAffiliateConfig(): AffiliateConfig {
  try {
    const g = (globalThis as { RVP_AFFILIATE?: unknown }).RVP_AFFILIATE;
    return g && typeof g === 'object' ? (g as AffiliateConfig) : {};
  } catch {
    return {};
  }
}
